import { useState, useRef, useEffect, useMemo } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalFooter,
   ModalCloseButton,
   useColorMode,
} from '@chakra-ui/react';
import axios from '../../../../axios';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from 'contexts';
import { DAYBOOK_TYPE } from 'constants';
import { getDaybookForPayments } from '../../../../redux/actions/daybook';
import { getSuspenseAccount } from '../../../../redux/actions/account';
import AddEditForm from 'views/admin/masters/Pages/CustomerMaster/AddEditForm';

export default function BankTransferModal({ isBankTransfer, onClose, handleBankTransfer }) {
   const dispatch = useDispatch();
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [selectedItems, setSelectedItems] = useState({});
   const [selectAll, setSelectAll] = useState(false);
   const [selectedBank, setSelectedBank] = useState('');
   const [selectedDaybook, setSelectedDaybook] = useState('');
   const suspenseAcc = useSelector((state) => state.account.suspense_list || []);
   const daybookTypeData = useSelector((state) => state.daybook?.listPayment || null);
   const [selectedFile, setSelectedFile] = useState(null);
   const [suspenseAccount, setSuspenseAccount] = useState(null);
   const [bankStatements, setBankStatements] = useState([]);
   const [distinctStatements, setDistinctStatements] = useState([]);
   const [selectedStatements, setSelectedStatements] = useState({});
   const [selectedAllStatements, setSelectedAllStatements] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isImporting, setIsImporting] = useState(false);
   const { user } = useUser();
   const [message, setMessage] = useState('');
   const fileInputRef = useRef(null);
   const [isOpen, setIsOpen] = useState(false);
   const [isEditMode, setIsEditMode] = useState(false);
   const [currentRow, setCurrentRow] = useState({});
   const [activeTab, setActiveTab] = useState(0);

   // Updated formData structure for bulk import
   const [formData, setFormData] = useState({
      companyId: parseInt(localStorage.getItem('companyId')),
      daybookId: null,
      vouchers: [],
   });

   const bankOptions = [
      { value: '1', label: 'SBI' },
      { value: '2', label: 'Indusnd Bank' },
      { value: '3', label: 'Fedral Bank' },
      { value: '4', label: 'ICICI Bank' },
      { value: '5', label: 'Indian Bank' },
      { value: '6', label: 'Paytm Bank' },
      { value: '7', label: 'Kalyan' },
      { value: '8', label: 'BOB Bank' },
      { value: '9', label: 'Union Bank' },
      { value: '10', label: 'Varacha Bank' },
      { value: '11', label: 'Surat Bank' },
      { value: '12', label: 'Akhand Anand Bank' },
      { value: '13', label: 'Canaera Bank' },
      { value: '14', label: 'CBI Bank' },
      { value: '15', label: 'HDFC Bank' },
      { value: '16', label: 'Axis Bank' },
      { value: '17', label: 'AU Bank' },
   ];

   const masterItems = [
      { code: 'Account', name: 'Account' },
      { code: 'Items', name: 'Item' },
      { code: 'Category', name: 'Category' },
      { code: 'Brand', name: 'Brand' },
      { code: 'Serise', name: 'Serise' },
      { code: 'Item Group', name: 'Item Group' },
      { code: 'Warehouse', name: 'Warehouse' },
      { code: 'Unit', name: 'Unit' },
      { code: 'Screen', name: 'Screen' },
      { code: 'Bank Branch', name: 'Bank Branch' },
      { code: 'Transport', name: 'Transport' },
      { code: 'Driver', name: 'Driver' },
      { code: 'Vehicle', name: 'Vehicle' },
      { code: 'Color', name: 'Color' },
      { code: 'Style', name: 'Style' },
      { code: 'Size', name: 'Size' },
   ];

   const userCompanyId = Number(user?.loginhistory?.companyId);
   const localCompanyId = Number(localStorage.getItem('companyId'));
   const validCompanyId = useMemo(() => {
      return !isNaN(userCompanyId) && userCompanyId > 0
         ? userCompanyId
         : !isNaN(localCompanyId) && localCompanyId > 0
            ? localCompanyId
            : null;
   }, [userCompanyId, localCompanyId]);

   const clearAllData = () => {
      setSelectedItems({});
      setSelectAll(false);
      setSelectedBank('');
      setSelectedDaybook('');
      setSelectedFile(null);
      setBankStatements([]);
      setDistinctStatements([]);
      setSelectedStatements({});
      setSelectedAllStatements({});
      setIsLoading(false);
      setIsImporting(false);
      setMessage('');
      setIsOpen(false);
      setIsEditMode(false);
      setCurrentRow({});
      setActiveTab(0);
      setFormData({
         companyId: parseInt(localStorage.getItem('companyId')),
         daybookId: null,
         vouchers: [],
      });

      if (fileInputRef.current) {
         fileInputRef.current.value = '';
      }
   };

   const handleModalClose = () => {
      clearAllData();
      onClose();
   };

   useEffect(() => {
      const fetchInitialData = async () => {
         if (validCompanyId) {
            try {
               await dispatch(getSuspenseAccount({ name: 'SUSPENCE', compId: validCompanyId, page: 1, limit: 10 }));
            } catch (error) {
               console.error('Error fetching initial data:', error);
            }
         } else {
            console.warn('Skipping initial data fetch: Invalid companyId');
         }
      };

      fetchInitialData();
   }, [dispatch, validCompanyId]);

   useEffect(() => {
      const updateSuspenseAccount = async () => {
         if (suspenseAcc && suspenseAcc.length > 0) {
            setSuspenseAccount(suspenseAcc[0].id);
         } else {
            setSuspenseAccount(null);
         }
      };

      updateSuspenseAccount();
   }, [suspenseAcc]);

   const handleSave = () => {
      setIsOpen(false);
      if (validCompanyId) {
         dispatch(getSuspenseAccount({ name: 'SUSPENCE', compId: validCompanyId, page: 1, limit: 10 }));
      }
      setMessage('Suspense Account created. Please try importing again.');
   };

   const getLastVoucherDocNumber = async (companyId, daybookId, txnType) => {
      try {
         const response = await axios.get(
            `/voucher/last?companyId=${companyId}&daybookId=${daybookId}&txnType=${txnType}`,
         );
         if (response.data.code === 1) {
            const docNumber = response.data.data.docNumber;
            return parseInt(docNumber) + 1;
         }
         return 1;
      } catch (error) {
         console.error('Error fetching last voucher:', error);
         return 1;
      }
   };

   const parseDate = (dateStr) => {
      try {
         const date = new Date(dateStr);
         if (isNaN(date.getTime())) {
            return new Date().toISOString().split('T')[0];
         }
         return date.toISOString().split('T')[0];
      } catch (error) {
         return new Date().toISOString().split('T')[0];
      }
   };

   const createVoucherFromStatement = async (statement, companyId, daybookId, docNumber) => {
      const hasCredit = statement.credit && parseFloat(statement.credit) > 0;
      const hasDebit = statement.debit && parseFloat(statement.debit) > 0;

      const txnType = hasCredit ? 1 : 0;
      const amount = hasCredit ? parseFloat(statement.credit) : parseFloat(statement.debit || 0);

      return {
         companyId: companyId,
         daybookId: daybookId,
         docNumber: docNumber.toString(),
         txnType: txnType,
         txnDate: parseDate(statement.date),
         accountId: suspenseAccount,
         amount: amount,
         brokerId: 0,
         supplierId: 0,
         chequeNo: null,
         transactionNo: statement.ref_no_cheque_no || '',
         bankId: 0,
         bankBranchId: 0,
         particulars: '',
         depositeDate: null,
         clearingDate: null,
         tdsApplicable: false,
         tdsAccountId: 0,
         tdsLimit: 0,
         tdsRate: 0,
         tdsCess: 0,
         tdsSurcharge: 0,
         tdsAmount: 0,
         tdsCessAmount: 0,
         tdsSurchargeAmount: 0,
         tdsHCessAmount: 0,
         tdsInterestAmount: 0,
         tdsPenaltyAmount: 0,
         tdsOthersAmount: 0,
         notes1: '',
         notes2: '',
         notes3: '',
         notes4: '',
         notes5: '',
         userNote: statement.description || '',
         taxableValue: '',
         GSTAmount: 0,
         stateOfSupply: 0,
         isRCM: false,
         GSTSubTxnType: 0,
         HSNSACId: 0,
         typeofSupply: null,
         partyBillNo: 0,
         GSTTxnType: 0,
         chequeDate: null,
         outAdjustments: [],
         isImported: true,
      };
   };

   const handleFileSelect = (event) => {
      const file = event.target.files[0];
      setSelectedFile(file);
      setBankStatements([]);
      setDistinctStatements([]);
      setSelectedStatements({});
      setSelectedAllStatements({});
      setMessage('');
   };

   const extractBankStatement = async () => {
      if (!selectedFile || !selectedBank) {
         setMessage('Please select both bank and file');
         return;
      }

      setIsLoading(true);
      setMessage('');

      try {
         const formData = new FormData();
         formData.append('file', selectedFile);
         formData.append('bank', selectedBank);
         formData.append('companyId', parseInt(validCompanyId));
         formData.append('accountId', parseInt(suspenseAccount));

         const response = await axios.post(`/bank-branch/extract`, formData, {
            headers: {
               'Content-Type': 'multipart/form-data',
            },
         });

         const data = response.data.data;
         setBankStatements(data.transactions || []);

         const descriptionMap = new Map();
         data.transactions?.forEach((statement, index) => {
            const desc = statement.description;
            if (!descriptionMap.has(desc)) {
               descriptionMap.set(desc, { ...statement, originalIndex: index, count: 1 });
            } else {
               descriptionMap.get(desc).count++;
            }
         });

         const distinctData = Array.from(descriptionMap.values());
         setDistinctStatements(distinctData);

         setMessage(
            `Extracted ${data.transactions?.length || 0} records, ${distinctData.length} distinct descriptions`,
         );
      } catch (error) {
         setMessage(`Error: ${error.response.data.message}`);
      } finally {
         setIsLoading(false);
      }
   };

   const toggleAllStatementSelection = (index) => {
      setSelectedAllStatements((prev) => ({
         ...prev,
         [index]: !prev[index],
      }));
   };

   const importSelectedAllStatements = async () => {
      const selectedData = bankStatements.filter((_, index) => selectedAllStatements[index]);
      if (selectedData.length === 0) {
         setMessage('Please select at least one statement to import');
         return;
      }

      if (!selectedDaybook) {
         setMessage('Please select a daybook');
         return;
      }

      setIsImporting(true);
      setMessage('');

      try {
         const companyId = parseInt(localStorage.getItem('companyId'));
         const daybookId = parseInt(selectedDaybook);

         const sanitizeText = (text) => {
            if (!text) return text;
            return text
               .replace(/\n/g, ' ')
               .replace(/\r/g, ' ')
               .replace(/\s+/g, ' ')
               .replace(/\s*-\s*/g, '-')
               .replace(/\s*\/\s*/g, '/')
               .trim();
         };

         const creditStatements = [];
         const debitStatements = [];
         for (let i = 0; i < selectedData.length; i++) {
            const statement = { ...selectedData[i] };
            if (statement.description) {
               statement.description = sanitizeText(statement.description);
            }
            if (statement.ref_no_cheque_no) {
               statement.ref_no_cheque_no = sanitizeText(statement.ref_no_cheque_no);
            }
            const hasCredit = statement.credit && parseFloat(statement.credit) > 0;
            if (hasCredit) {
               creditStatements.push(statement);
            } else {
               debitStatements.push(statement);
            }
         }

         const vouchers = [];

         if (creditStatements.length > 0) {
            const creditDocNumber = (await getLastVoucherDocNumber(companyId, daybookId, 1)) || 1;
            for (let i = 0; i < creditStatements.length; i++) {
               const statement = creditStatements[i];
               const docNumber = creditDocNumber + i;
               const voucher = await createVoucherFromStatement(statement, companyId, daybookId, docNumber);
               vouchers.push(voucher);
            }
         }

         if (debitStatements.length > 0) {
            const debitDocNumber = (await getLastVoucherDocNumber(companyId, daybookId, 0)) || 1;
            for (let i = 0; i < debitStatements.length; i++) {
               const statement = debitStatements[i];
               const docNumber = debitDocNumber + i;
               const voucher = await createVoucherFromStatement(statement, companyId, daybookId, docNumber);
               vouchers.push(voucher);
            }
         }

         const bulkImportData = {
            vouchers: vouchers,
         };

         setFormData(bulkImportData);

         const response = await axios.post(`/voucher/bulk-import`, bulkImportData);

         if (response.data.code === 1) {
            setMessage(`Successfully imported ${selectedData.length} vouchers`);
            setSelectedAllStatements({});
         } else {
            throw new Error(response.data.message || 'Failed to import vouchers');
         }
      } catch (error) {
         setMessage(`Import Error: ${error?.response?.data?.message}`);
         console.error('Import error123:', error?.response?.data?.message);
      } finally {
         setIsImporting(false);
      }
   };

   if (!isBankTransfer) return null;

   const selectedAllCount = Object.values(selectedAllStatements).filter(Boolean).length;
   const isAllSelected = bankStatements.length > 0 && selectedAllCount === bankStatements.length;
   const isIndeterminate = selectedAllCount > 0 && selectedAllCount < bankStatements.length;

   return (
      <>
         <Modal
            isOpen={isBankTransfer}
            onClose={handleModalClose}
            size={{ base: 'full', md: '6xl' }}
            isCentered
            scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent
               bg={isDark ? 'gray.900' : 'white'}
               mx={{ base: 0, md: 4 }}
               my={{ base: 0, md: 4 }}
               maxH={{ base: '100vh', md: '95vh' }}
               borderWidth="1px"
               borderColor={isDark ? 'gray.700' : 'gray.200'}
               boxShadow="xl"
               borderRadius="xl"
               overflow="hidden">

               {/* Header with Gradient */}
               <ModalHeader
                  display="flex"
                  alignItems="center"
                  px={{ base: 4, md: 6 }}
                  py={{ base: 4, md: 5 }}
                  borderBottomWidth="1px"
                  borderColor={isDark ? 'gray.700' : 'gray.200'}
                  style={{
                     background: isDark
                        ? 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 50%, #1e40af 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #2563eb 100%)'
                  }}>
                  <h2 className="text-lg md:text-2xl font-bold text-white tracking-tight">
                     Bank Transfer Manager
                  </h2>
               </ModalHeader>

               <ModalCloseButton
                  size="lg"
                  color="white"
                  _hover={{ bg: 'blackAlpha.300' }}
                  _active={{ bg: 'blackAlpha.400' }}
               />

               <ModalBody px={{ base: 4, md: 6 }} py={{ base: 5, md: 6 }}>
                  <div className="space-y-6">

                     {/* Modern Upload Card */}
                     <div className={`relative overflow-hidden rounded-2xl shadow-lg border-2 ${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
                        }`}>
                        {/* Decorative Background Pattern */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 opacity-5 rounded-full blur-3xl -mr-32 -mt-32"></div>

                        <div className="relative p-6 md:p-8">
                           <div className="flex items-center gap-3 mb-6">
                              <div className="p-2 rounded-lg bg-blue-500 bg-opacity-10">
                                 <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                 </svg>
                              </div>
                              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                 Upload Bank Statement
                              </h3>
                           </div>

                           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                              {/* Bank Select with Icon */}
                              <div className="space-y-2">
                                 <label className={`flex items-center gap-2 text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    Select Bank
                                 </label>
                                 <div className="relative">
                                    <select
                                       value={selectedBank}
                                       onChange={(e) => setSelectedBank(e.target.value)}
                                       className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2 font-medium transition-all ${isDark
                                          ? 'bg-gray-900 border-gray-700 text-white hover:border-blue-500 focus:border-blue-500 focus:ring-4 focus:ring-blue-500 focus:ring-opacity-20'
                                          : 'bg-white border-gray-200 text-gray-900 hover:border-blue-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500 focus:ring-opacity-20'
                                          } focus:outline-none shadow-sm`}
                                    >
                                       <option value="">Choose your bank...</option>
                                       {bankOptions.map((bank, index) => (
                                          <option key={`${bank.value}-${index}`} value={bank.value}>
                                             {bank.label}
                                          </option>
                                       ))}
                                    </select>
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                       <svg className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                       </svg>
                                    </div>
                                 </div>
                              </div>

                              {/* File Upload with Drag & Drop Style */}
                              <div className="space-y-2">
                                 <label className={`flex items-center gap-2 text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Upload Statement File
                                 </label>
                                 <div className={`relative border-2 border-dashed rounded-xl transition-all ${isDark
                                    ? 'border-gray-700 hover:border-blue-500 bg-gray-900'
                                    : 'border-gray-300 hover:border-blue-400 bg-white'
                                    }`}>
                                    <input
                                       type="file"
                                       ref={fileInputRef}
                                       onChange={handleFileSelect}
                                       accept=".pdf,.csv,.xlsx,.xls"
                                       className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    />
                                    <div className="flex items-center gap-3 p-3">
                                       <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                          <svg className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                          </svg>
                                       </div>
                                       <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium truncate ${selectedFile
                                             ? (isDark ? 'text-blue-400' : 'text-blue-600')
                                             : (isDark ? 'text-gray-400' : 'text-gray-500')
                                             }`}>
                                             {selectedFile ? selectedFile.name : 'Click or drag file here'}
                                          </p>
                                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                             PDF, CSV, XLSX, XLS
                                          </p>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           </div>

                           {/* Extract Button - Full Width */}
                           <button
                              onClick={extractBankStatement}
                              disabled={!selectedFile || !selectedBank || isLoading}
                              className={`w-full py-4 rounded-xl font-bold text-white text-lg transition-all transform ${!selectedFile || !selectedBank || isLoading
                                 ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                 : 'hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98] shadow-lg'
                                 }`}
                              style={{
                                 background: (!selectedFile || !selectedBank || isLoading) ? undefined : (
                                    isDark
                                       ? 'linear-gradient(135deg, #1e40af 0%, #3730a3 50%, #4f46e5 100%)'
                                       : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #3b82f6 100%)'
                                 )
                              }}
                           >
                              {isLoading ? (
                                 <span className="flex items-center justify-center gap-3">
                                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    <span>Extracting Bank Data...</span>
                                 </span>
                              ) : (
                                 <span className="flex items-center justify-center gap-2">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    Extract Bank Statements
                                 </span>
                              )}
                           </button>

                           {/* Message Alert - Enhanced */}
                           {message && (
                              <div className={`mt-6 flex items-start gap-4 p-4 rounded-xl border-l-4 ${message.includes('Error')
                                 ? 'bg-red-50 border-red-500 dark:bg-red-900/20 dark:border-red-500'
                                 : 'bg-green-50 border-green-500 dark:bg-green-900/20 dark:border-green-500'
                                 }`}>
                                 <div className={`p-1 rounded-full ${message.includes('Error') ? 'bg-red-800 dark:bg-red-900/40' : 'bg-green-800 dark:bg-green-900/40'
                                    }`}>
                                    <svg className={`w-5 h-5 ${message.includes('Error') ? 'text-red-600 dark:text-red-800' : 'text-green-600 dark:text-green-800'
                                       }`} fill="currentColor" viewBox="0 0 20 20">
                                       {message.includes('Error') ? (
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                       ) : (
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                       )}
                                    </svg>
                                 </div>
                                 <p className={`text-sm font-semibold flex-1 ${message.includes('Error')
                                    ? 'text-red-800 dark:text-red-800'
                                    : 'text-green-900 dark:text-green-900'
                                    }`}>{message}</p>
                              </div>
                           )}
                        </div>
                     </div>

                     {(distinctStatements.length > 0 || bankStatements.length > 0) && (
                        <div className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'
                           } p-4 md:p-5 rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'
                           } shadow-sm`}>

                           <div className={`flex gap-1 p-1 rounded-lg mb-4 overflow-x-auto ${isDark ? 'bg-gray-700' : 'bg-gray-100'
                              }`} style={{ scrollbarWidth: 'none' }}>
                              <button
                                 onClick={() => setActiveTab(0)}
                                 className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-md font-medium transition-all whitespace-nowrap ${activeTab === 0
                                    ? 'text-white shadow-sm'
                                    : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                 style={activeTab === 0 ? {
                                    background: isDark
                                       ? 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 50%, #1e40af 100%)'
                                       : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #2563eb 100%)'
                                 } : undefined}
                              >
                                 <span className="text-sm md:text-base">All Statements</span>
                                 {bankStatements.length > 0 && (
                                    <span className="px-2 py-0.5 text-xs md:text-sm bg-green-600 text-white rounded-full font-semibold">
                                       {bankStatements.length}
                                    </span>
                                 )}
                              </button>
                              <button
                                 onClick={() => setActiveTab(1)}
                                 className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-md font-medium transition-all whitespace-nowrap ${activeTab === 1
                                    ? 'text-white shadow-sm'
                                    : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                 style={activeTab === 1 ? {
                                    background: isDark
                                       ? 'linear-gradient(135deg, #0f766e 0%, #115e59 50%, #134e4a 100%)'
                                       : 'linear-gradient(135deg, #0d9488 0%, #0f766e 50%, #14b8a6 100%)'
                                 } : undefined}
                              >
                                 <span className="text-sm md:text-base">Distinct Statements</span>
                                 {distinctStatements.length > 0 && (
                                    <span className="px-2 py-0.5 text-xs md:text-sm bg-blue-600 text-white rounded-full font-semibold">
                                       {distinctStatements.length}
                                    </span>
                                 )}
                              </button>
                           </div>

                           {activeTab === 0 && bankStatements.length > 0 && (
                              <div>
                                 <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 md:gap-0 mb-4">
                                    <h3 className={`text-sm md:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'
                                       }`}>
                                       All Bank Statements ({bankStatements.length})
                                    </h3>

                                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                       <div className="w-full sm:w-64 md:w-80">
                                          <label className={`block text-sm md:text-base font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'
                                             }`}>
                                             Select Daybook
                                          </label>
                                          <select
                                             value={selectedDaybook}
                                             onChange={(e) => setSelectedDaybook(e.target.value)}
                                             className={`w-full px-4 py-2.5 md:py-3 rounded-lg border ${isDark
                                                ? 'bg-gray-900 border-gray-700 text-white hover:border-blue-500 focus:border-blue-500'
                                                : 'bg-white border-gray-300 text-gray-900 hover:border-blue-600 focus:border-blue-600'
                                                } focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors`}
                                          >
                                             <option value="">Choose a Daybook</option>
                                             {daybookTypeData?.map((daybook, index) => (
                                                <option key={`${daybook.id}-${index}`} value={daybook.id}>
                                                   {daybook.name}
                                                </option>
                                             ))}
                                          </select>
                                       </div>

                                       <button
                                          onClick={importSelectedAllStatements}
                                          disabled={Object.values(selectedAllStatements).every((v) => !v) || !selectedDaybook || isImporting}
                                          className={`self-end px-6 py-2.5 md:py-3 rounded-lg font-semibold text-white transition-all ${Object.values(selectedAllStatements).every((v) => !v) || !selectedDaybook || isImporting
                                             ? 'bg-gray-400 cursor-not-allowed'
                                             : 'hover:translate-y-[-1px] hover:shadow-lg active:translate-y-0'
                                             }`}
                                          style={{
                                             background: (Object.values(selectedAllStatements).every((v) => !v) || !selectedDaybook || isImporting) ? undefined : (
                                                isDark
                                                   ? 'linear-gradient(135deg, #0f766e 0%, #115e59 50%, #134e4a 100%)'
                                                   : 'linear-gradient(135deg, #0d9488 0%, #0f766e 50%, #14b8a6 100%)'
                                             )
                                          }}
                                       >
                                          {isImporting ? (
                                             <span className="flex items-center gap-2">
                                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Importing...
                                             </span>
                                          ) : 'Import Selected'}
                                       </button>
                                    </div>
                                 </div>

                                 <div className={`border rounded-xl overflow-hidden shadow-sm ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
                                    }`}>
                                    <div className="max-h-96 md:max-h-[500px] overflow-auto">
                                       <table className="w-full min-w-[800px]">
                                          <thead className={`sticky top-0 z-10 ${isDark ? 'bg-gray-700' : 'bg-gray-100'
                                             }`}>
                                             <tr>
                                                <th className={`px-3 md:px-4 py-3 text-left border-b ${isDark ? 'border-gray-600' : 'border-gray-300'
                                                   }`}>
                                                   <div className="flex flex-col items-start gap-1">
                                                      <input
                                                         type="checkbox"
                                                         checked={isAllSelected}
                                                         ref={(el) => {
                                                            if (el) el.indeterminate = isIndeterminate;
                                                         }}
                                                         onChange={(e) => {
                                                            const isChecked = e.target.checked;
                                                            const newSelection = {};
                                                            bankStatements.forEach((_, index) => {
                                                               newSelection[index] = isChecked;
                                                            });
                                                            setSelectedAllStatements(newSelection);
                                                         }}
                                                         className="w-4 h-4 md:w-5 md:h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                      />
                                                      <span className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'
                                                         }`}>
                                                         ({selectedAllCount})
                                                      </span>
                                                   </div>
                                                </th>
                                                <th className={`px-3 md:px-4 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Date</th>
                                                <th className={`px-3 md:px-4 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Description</th>
                                                <th className={`px-3 md:px-4 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Credit</th>
                                                <th className={`px-3 md:px-4 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Debit</th>
                                                <th className={`px-3 md:px-4 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Balance</th>
                                                <th className={`px-3 md:px-4 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Type</th>
                                             </tr>
                                          </thead>
                                          <tbody>
                                             {bankStatements.map((statement, index) => (
                                                <tr
                                                   key={index}
                                                   className={`border-b transition-colors ${isDark
                                                      ? 'border-gray-700 hover:bg-gray-800'
                                                      : 'border-gray-200 hover:bg-gray-50'
                                                      }`}
                                                >
                                                   <td className="px-3 md:px-4 py-3">
                                                      <input
                                                         type="checkbox"
                                                         checked={selectedAllStatements[index] || false}
                                                         onChange={() => toggleAllStatementSelection(index)}
                                                         className="w-4 h-4 md:w-5 md:h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                      />
                                                   </td>
                                                   <td className={`px-3 md:px-4 py-3 text-sm md:text-base whitespace-nowrap font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'
                                                      }`}>
                                                      {statement.txn_date}
                                                   </td>
                                                   <td className={`px-3 md:px-4 py-3 text-sm md:text-base max-w-[150px] md:max-w-[250px] whitespace-pre-wrap ${isDark ? 'text-white' : 'text-gray-900'
                                                      }`}>
                                                      {statement.description}
                                                   </td>
                                                   <td className="px-3 md:px-4 py-3 text-sm md:text-base text-right text-green-500 font-semibold">
                                                      {statement.credit || '-'}
                                                   </td>
                                                   <td className="px-3 md:px-4 py-3 text-sm md:text-base text-right text-red-500 font-semibold">
                                                      {statement.debit || '-'}
                                                   </td>
                                                   <td className={`px-3 md:px-4 py-3 text-sm md:text-base text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'
                                                      }`}>
                                                      {statement.balance || '-'}
                                                   </td>
                                                   <td className="px-3 md:px-4 py-3">
                                                      <span className={`inline-block px-3 py-1 text-xs md:text-sm font-semibold rounded-full ${statement.credit && parseFloat(statement.credit) > 0
                                                         ? 'bg-green-100 text-green-800'
                                                         : 'bg-red-100 text-red-800'
                                                         }`}>
                                                         {statement.credit && parseFloat(statement.credit) > 0 ? 'Credit' : 'Debit'}
                                                      </span>
                                                   </td>
                                                </tr>
                                             ))}
                                          </tbody>
                                       </table>
                                    </div>
                                 </div>
                              </div>
                           )}

                           {activeTab === 1 && distinctStatements.length > 0 && (
                              <div>
                                 <h3 className={`text-sm md:text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'
                                    }`}>
                                    Distinct Bank Statements ({distinctStatements.length})
                                 </h3>

                                 <div className={`border rounded-xl overflow-hidden shadow-sm ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
                                    }`}>
                                    <div className="max-h-96 md:max-h-[500px] overflow-auto">
                                       <table className="w-full min-w-[600px]">
                                          <thead className={`sticky top-0 z-10 ${isDark ? 'bg-gray-700' : 'bg-gray-100'
                                             }`}>
                                             <tr>
                                                <th className={`px-4 md:px-6 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Date</th>
                                                <th className={`px-4 md:px-6 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Description</th>
                                                <th className={`px-4 md:px-6 py-3 text-left text-sm md:text-base font-bold border-b ${isDark ? 'text-white border-gray-600' : 'text-gray-900 border-gray-300'
                                                   }`}>Count</th>
                                             </tr>
                                          </thead>
                                          <tbody>
                                             {distinctStatements.map((statement, index) => (
                                                <tr
                                                   key={index}
                                                   className={`border-b transition-colors ${isDark
                                                      ? 'border-gray-700 hover:bg-gray-800'
                                                      : 'border-gray-200 hover:bg-gray-50'
                                                      }`}
                                                >
                                                   <td className={`px-4 md:px-6 py-3 text-sm md:text-base whitespace-nowrap font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'
                                                      }`}>
                                                      {statement.txn_date}
                                                   </td>
                                                   <td className={`px-4 md:px-6 py-3 text-sm md:text-base max-w-[200px] md:max-w-[300px] whitespace-pre-wrap ${isDark ? 'text-white' : 'text-gray-900'
                                                      }`}>
                                                      {statement.description}
                                                   </td>
                                                   <td className="px-4 md:px-6 py-3 text-center">
                                                      <span
                                                         className="inline-block px-3 py-1 text-sm md:text-base font-semibold rounded-full text-white"
                                                         style={{
                                                            background: isDark
                                                               ? 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 50%, #1e40af 100%)'
                                                               : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #2563eb 100%)'
                                                         }}
                                                      >
                                                         {statement.count}
                                                      </span>
                                                   </td>
                                                </tr>
                                             ))}
                                          </tbody>
                                       </table>
                                    </div>
                                 </div>
                              </div>
                           )}
                        </div>
                     )}

                     <hr className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-300'}`} />
                  </div>
               </ModalBody>

               <ModalFooter
                  justifyContent={{ base: 'stretch', md: 'flex-end' }}
                  px={{ base: 4, md: 6 }}
                  py={{ base: 4, md: 5 }}
                  bg={isDark ? 'gray.800' : 'gray.50'}
                  borderTopWidth="1px"
                  borderColor={isDark ? 'gray.700' : 'gray.200'}>
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 w-full md:w-auto">
                     <button
                        onClick={onClose}
                        className={`px-6 py-2.5 md:py-3 rounded-lg font-medium border transition-colors ${isDark
                           ? 'border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-600'
                           : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400'
                           }`}
                     >
                        Close
                     </button>
                     <button
                        onClick={importSelectedAllStatements}
                        disabled={Object.values(selectedAllStatements).every((v) => !v) || !selectedDaybook || isImporting}
                        className={`px-6 py-2.5 md:py-3 rounded-lg font-semibold text-white transition-all ${Object.values(selectedAllStatements).every((v) => !v) || !selectedDaybook || isImporting
                           ? 'bg-gray-400 cursor-not-allowed'
                           : 'hover:translate-y-[-1px] hover:shadow-lg active:translate-y-0'
                           }`}
                        style={{
                           background: (Object.values(selectedAllStatements).every((v) => !v) || !selectedDaybook || isImporting) ? undefined : (
                              isDark
                                 ? 'linear-gradient(135deg, #0f766e 0%, #115e59 50%, #134e4a 100%)'
                                 : 'linear-gradient(135deg, #0d9488 0%, #0f766e 50%, #14b8a6 100%)'
                           )
                        }}
                     >
                        {isImporting ? (
                           <span className="flex items-center gap-2">
                              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Importing...
                           </span>
                        ) : 'Import Selected'}
                     </button>
                  </div>
               </ModalFooter>
            </ModalContent>
         </Modal>

         <AddEditForm
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            isEditMode={isEditMode}
            currentRow={currentRow}
            initialData={currentRow}
            parentGlGroup={31}
            onSave={handleSave}
            demoname={'SUSPENCE'}
            formType="Account"
            saveandcontinue={false}
         />
      </>
   );
}